import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { getRoute, SUBSCRIPTION } from 'routes';
import {
	XC_URL,
	PACKAGES_CONFIG,
	SUBSCRIPTION_PERIOD,
	CURRENCIES,
	DESKTOP_APPS,
	MAP_ADDON_TO_LATEST_VERSION,
	SUBSCRIPTION_VIEW,
	MAXIC_DISCOUNT_PROD_TYPE
} from '../../config';
import { SuccessIcon } from '../../theme/icons';
import { 
	convertPlanIdIntoSubscription,
	isDesktopOnlyPlan,
	isPerpetualPlan,
	getTotalProductsForCompany
} from '../../utils/subscription';
import { trackAndDownload, getAddonDataForDownload } from '../../utils/downloads';
import { clearCrsCredentials, updateUsage } from '../../redux/modules/crs';
import { clearAppliedDiscount } from '../../redux/modules/discount';
import { writeUserSettings } from 'redux/modules/session';
import { 
	getCheckoutPlan,
	getIsFirstOrder,
	getCheckoutSeatsQuantity,
	getReUpgradePopupPlan,
	clearReUpgradePopupPlan,
	getIsReUpgradePopupFlag,
	clearIsReUpgradePopupFlag
} from '../../utils/storage';
import { trackSubscriptionSuccessful, trackPaywallConverstion } from 'lib/Segment/customTrackers';
import { getTrackingOptions } from 'lib/Segment/helpers';
import { getPendingPerpetualPlans } from '../../utils/subscription';

import i18n from 'i18n/i18n';
import _ from 'lodash';
import { 
	getMagixDiscountCredentials,
	clearMagixDiscountCredentials,
} from '../../utils/storage';

import { getDesktopAppFlag } from '../../redux/modules/app';
import { writeCompanySettings } from '../../redux/modules/companies';
import { stripHTML } from 'utils/string';

const localize = i18n.loc.localize;

const styles = theme => ({
	successMessageContainer: {
		textAlign: 'center',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		minHeight: '400px'
	},
	title: {
		fontSize: '22px',
		lineHeight: '25px',
		margin: '0 0 1rem',
		fontWeight: 'bold',
		opacity: 1
	},
	description:  {
		opacity: 1,
		color: '#2E3F4F',
		marginBottom: 10
	},
	currentlySignedInTitle: {
		fontSize: '16px',
		lineHeight: '24px',
		fontWeight: 'bold',
		color: '#2E3F4F',
		opacity: 1
	},
	successAnimationSVG: {
		height: '56px',
		width: '56px',
		margin: '0 auto 1.5rem',
		color: '#2FA5E7'
	},
	buttonsContainer: {
		padding: '53px 0 0'
	},
	buttonRoot: {
		margin: '0 5px',
		minWidth: '157px'
	}
});
@withStyles(styles)
class SubscriptionSuccessful extends Component {

	constructor(props) {
		super(props);

		const savedCheckoutPlanId = getCheckoutPlan();

		const savedCheckoutSeatsQuantity = getCheckoutSeatsQuantity();
		const wasPerpertualPlanPurchased = isPerpetualPlan(savedCheckoutPlanId);
		const isDesktopOnly = isDesktopOnlyPlan(savedCheckoutPlanId);

		const activeSubscription = convertPlanIdIntoSubscription(savedCheckoutPlanId);
		const { plan } = activeSubscription;

		if (plan) {
			let planName;
			if (wasPerpertualPlanPurchased) {
				const addonData = getAddonDataForDownload(MAP_ADDON_TO_LATEST_VERSION[plan]);
				planName = addonData.title;
			} else {
				planName = PACKAGES_CONFIG[plan].title;
			}

			this.state = {
				savedCheckoutPlanId,
				planName: stripHTML(planName),
				...(savedCheckoutSeatsQuantity && { savedCheckoutSeatsQuantity }),
				isDesktopOnly,
				wasPerpertualPlanPurchased
			};

		} else {
			this.state = {
				missingPlanId: true
			};			
		}
	}

	render() {
		const { classes, selectedCompanyId } = this.props;

		if (this.state.missingPlanId) {
			return (
				<div className={classes.successMessageContainer}>

					<SuccessIcon className={classes.successAnimationSVG} />
					<Typography className={classes.title} variant="h2">
						{localize('purchase-successful-thanks')}
					</Typography>					

					<div className={classes.buttonsContainer}>
						<Button
							data-tid="button-success-see-my-plans"
							variant='outlined'
							color='primary'
							classes={{
								root: classes.buttonRoot
							}}
							onClick={() => {
								this.props.history.push({
									pathname: getRoute(SUBSCRIPTION, selectedCompanyId),
									state: { preselectSubscriptionView: SUBSCRIPTION_VIEW.CURRENT_PRODUCTS }
								});
							}}>
							{localize('subscription-successful-see-plans')}
						</Button>
					</div>
				</div>
			);			
		}
		
		const nrOfProductUserOwns = getTotalProductsForCompany(this.props.company);

		if (nrOfProductUserOwns === 0 && !this.state.wasPerpertualPlanPurchased) {
			return (
				<div className={classes.successMessageContainer}>
					<Typography className={classes.title} variant="h2">
						{localize('subscription-successful-not-found')}
					</Typography>	
				</div>
			);			
		}

		return (
			<div className={classes.successMessageContainer}>

				<SuccessIcon className={classes.successAnimationSVG} />
				<Typography className={classes.title} variant="h2">
					{localize(this.state.wasPerpertualPlanPurchased
						?
						'purchase-successful-thanks'
						:
						'subscription-successful-thanks')}
				</Typography>

				{this.state.isDesktopOnly
					?
					(
						<React.Fragment>
							<Typography variant="body1" className={classes.description}>
								{localize(this.state.wasPerpertualPlanPurchased
									?
									'subscription-successful-purchased'
									:
									'subscription-successful-upgraded-to'
								)} <strong>{this.state.planName}</strong>.
							</Typography>
							<Typography
								variant="body1"
								className={classes.description}
								dangerouslySetInnerHTML={{ __html: 
									localize('subscription-successful-activation-email', _.get(this.props.user, 'mainEmail'))
								}}
							/>

							<div className={classes.buttonsContainer}>
								<Button
									data-tid="button-success-see-my-plans"
									variant='outlined'
									color='primary'
									classes={{
										root: classes.buttonRoot
									}}
									onClick={() => {
										this.props.history.push({
											pathname: getRoute(SUBSCRIPTION, selectedCompanyId),
											state: { preselectSubscriptionView: SUBSCRIPTION_VIEW.CURRENT_PRODUCTS }
										});
									}}>
									{localize('subscription-successful-see-plans')}
								</Button>

								<Button
									data-tid="button-download-product"
									variant='contained'
									color='primary'
									classes={{
										root: classes.buttonRoot
									}}
									onClick={() => {
										if (this.state.wasPerpertualPlanPurchased) {
											const plan = this.state.savedCheckoutPlanId.split('-')[0];
											const addonData = getAddonDataForDownload(MAP_ADDON_TO_LATEST_VERSION[plan]);
											
											this.onDownloadClick(
												addonData.downloadUrl,
												addonData.storageKey, 
												addonData.trackingKey);
										} else {
											const activeSubscription = convertPlanIdIntoSubscription(
												this.state.savedCheckoutPlanId
											);
											const { plan } = activeSubscription;
	
											const { downloadUrl, storageKey, trackingKey} = DESKTOP_APPS[plan.toUpperCase()];
											this.onDownloadClick(downloadUrl, storageKey, trackingKey);
										}
									}}>
									{localize('subscription-successful-download-products')}
								</Button>
							</div>
						</React.Fragment>
					)
					:
					(
						<React.Fragment>
							<Typography variant="body1" className={classes.description}>
								{localize('subscription-successful-upgraded')}
							</Typography>

							<div className={classes.buttonsContainer}>
								<Button
									data-tid="button-success-see-my-plans"
									variant='outlined'
									color='primary'
									classes={{
										root: classes.buttonRoot
									}}
									onClick={() => {
										this.props.history.push({
											pathname: getRoute(SUBSCRIPTION, selectedCompanyId),
											state: { preselectSubscriptionView: SUBSCRIPTION_VIEW.CURRENT_PRODUCTS }
										});
									}}>
									{localize('subscription-successful-see-plans')}
								</Button>

								<Button
									data-tid="button-success-start-creating"
									variant='contained'
									color='primary'
									classes={{
										root: classes.buttonRoot
									}}
									onClick={() => {
										window.location.href = XC_URL;
									}}>
									{localize('subscription-successful-start-creating')}
								</Button>
							</div>
						</React.Fragment>
					)
				}
			</div>
		);
	}

	componentDidMount() {
		if (this.state.savedCheckoutPlanId) {
			const activeSubscription = convertPlanIdIntoSubscription(this.state.savedCheckoutPlanId);
			const { period, plan } = activeSubscription;
			
			if (this.isCloudPlan()) {
				this.props.getDesktopAppFlag();
			}

			const savedCredentials = getMagixDiscountCredentials();
			// For initial purchase user gets redirected to external page
			// so when seeing this page redux this.props.crs.isDiscountApplied prop does not exist
			// but we can check for savedCredentials which is only saved if 
			// this.props.crs.isDiscountApplied is true during checkout

			if (savedCredentials &&
				(period === SUBSCRIPTION_PERIOD.ANNUAL || period === SUBSCRIPTION_PERIOD.PERPETUAL)) {
				
				if (savedCredentials) {
					// Save data to mkdb.crs_store db table;
					this.props.updateUsage(
						savedCredentials.email,
						savedCredentials.serial,
						_.get(this.props.user, 'mainEmail'),
						this.state.wasPerpertualPlanPurchased
							?
							MAXIC_DISCOUNT_PROD_TYPE.PERPETUAL
							:
							MAXIC_DISCOUNT_PROD_TYPE.SUBSCRIPTION
					);
					clearMagixDiscountCredentials();
					this.props.clearCrsCredentials();
					this.props.clearAppliedDiscount();

					// Update mxDiscountUsed flag only for subscriptions;
					// user can buy perpetual products unlimited times using magix discount
					if (period === SUBSCRIPTION_PERIOD.ANNUAL) {
						this.props.writeCompanySettings({mxDiscountUsed: true});
					}
				}
			}

			const isFirstOrder = getIsFirstOrder();
			if (isFirstOrder && this.props.subscriptionId && !this.state.wasPerpertualPlanPurchased) {
				/* eslint-disable no-undef */
				typeof _refersion !== 'undefined' && _refersion(() => {
					if (typeof _rfsn !== 'undefined') {
						_rfsn._addCart(this.props.subscriptionId);
						_rfsn._setSource('CHARGEBEE');
					}
				});
			}

			if (this.state.wasPerpertualPlanPurchased) {
				const { currency } = convertPlanIdIntoSubscription(this.state.savedCheckoutPlanId);
				if (currency === CURRENCIES.EUR.key) {
					this.writePendingPaymentToCompanySettings();
				}

				trackSubscriptionSuccessful(
					this.props.selectedCompanyId,
					this.state.savedCheckoutPlanId,
					'perpetual-order',
					getTrackingOptions(this.props.company)
				);
			} else {
				this.writePendingSubscriptionToCompanySettings();

				const reUpgradePopupPlan = getReUpgradePopupPlan();

				if (reUpgradePopupPlan === plan && getIsReUpgradePopupFlag()) {
					trackPaywallConverstion(plan, this.props.selectedCompanyId, getTrackingOptions(this.props.company));
					clearReUpgradePopupPlan();
					clearIsReUpgradePopupFlag();
				}

				trackSubscriptionSuccessful(
					this.props.selectedCompanyId,
					this.state.savedCheckoutPlanId,
					isFirstOrder ? 'first-order' : 'plan-upgrade',
					getTrackingOptions(this.props.company)
				);
			}
		}
	}

	isCloudPlan() {
		if (this.state.savedCheckoutPlanId) {
			const activeSubscription = convertPlanIdIntoSubscription(this.state.savedCheckoutPlanId);
			const { plan } = activeSubscription;
			return PACKAGES_CONFIG[plan].isCloudSuitePlan;
		} else {
			return false;
		}
	}

	writePendingPaymentToCompanySettings() {
		const pendingPerpetualPlans = getPendingPerpetualPlans(this.props.company);
		pendingPerpetualPlans[this.state.savedCheckoutPlanId] = true;
		this.props.writeCompanySettings({ pendingPerpetualPlans });
	}

	// write pending subscription to use it in the future in this company only
	writePendingSubscriptionToCompanySettings() {
		this.props.writeCompanySettings(
			{ 
				pendingSubscription: this.state.savedCheckoutPlanId,
				...(this.state.savedCheckoutSeatsQuantity && { pendingSeatsQuantity: this.state.savedCheckoutSeatsQuantity })
			},
		);
	}

	onDownloadClick(downloadUrl, storageKey, trackingKey) {
		trackAndDownload(this.props, downloadUrl, storageKey, trackingKey, getTrackingOptions(this.props.company));
	}
}

SubscriptionSuccessful.propTypes = {
	company: PropTypes.object,
	classes: PropTypes.object,
	companies: PropTypes.object,
	history: PropTypes.object,
	selectedCompanyId: PropTypes.string,
	clearCrsCredentials: PropTypes.func,
	clearAppliedDiscount: PropTypes.func,
	writeCompanySettings: PropTypes.func,
	crs: PropTypes.object.isRequired,
	updateUsage: PropTypes.func.isRequired,
	user: PropTypes.object.isRequired,
	getDesktopAppFlag: PropTypes.func,
	subscriptionId: PropTypes.string,
	settings: PropTypes.object,
	writeUserSettings: PropTypes.func, // action creator from session module
};

const mapStateToProps = state => ({
	crs: state.crs,
	user: state.session.user,
	settings: state.session.user.settings,
	company: state.companies.rawDataById[state.companies.selectedId],
	selectedCompanyId: state.companies.selectedId
});

const mapDispatchToProps = dispatch => bindActionCreators({
	writeUserSettings,
	getDesktopAppFlag,
	clearCrsCredentials,
	clearAppliedDiscount,
	writeCompanySettings,
	updateUsage,
}, dispatch);

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(SubscriptionSuccessful);


// WEBPACK FOOTER //
// ./src/containers/SubscriptionSuccessful/SubscriptionSuccessful.js